import type {
  IssuedCredential,
  PersonPage,
  PersonSummary,
  RecordActionMethod,
  Session,
} from '@sentra/contracts';
import { personStatusSchema, presenceStateSchema, roleHasPermission } from '@sentra/contracts';
import { useQueryClient } from '@tanstack/react-query';
import { LoaderCircle, Plus, Search } from 'lucide-react';
import { type ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router';

import { FailureAlert } from '@/api/failure';
import type { PersonRegisterState } from '@/api/queries';
import {
  MIN_SEARCH_LENGTH,
  queryKeys,
  useFieldDefinitions,
  usePersonDetail,
  usePersonRegister,
} from '@/api/queries';
import { saveText } from '@/api/save-file';
import { useReportingTimezone } from '@/app/providers/config';
import { Alert, AlertDescription, AlertTitle } from '@/design/components/alert';
import { Button } from '@/design/components/button';
import { Input } from '@/design/components/input';
import { Label } from '@/design/components/label';
import { NativeSelect } from '@/design/components/native-select';
import { useSession } from '@/features/auth/session';
import { PersonForm } from '@/features/persons/form/person-form';
import { firstNameOf } from '@/features/persons/person-name';
import { AmendScreen } from '@/features/persons/record/amend-screen';
import { RecordScreen } from '@/features/persons/record/record-screen';
import type { ManualAction } from '@/features/persons/table/acknowledgment';
import { AcknowledgmentDialog } from '@/features/persons/table/acknowledgment';
import { csvFileName, toCsv } from '@/features/persons/table/csv';
import { PersonTable } from '@/features/persons/table/person-table';
import { ROWS_PER_PAGE } from '@/features/persons/table/table-model';
import type { RegisterView } from '@/features/persons/table/url-state';
import { DEFAULT_VIEW, paramsFromView, viewFromParams } from '@/features/persons/table/url-state';
import { useDebounced } from '@/features/persons/use-debounced';
import type { IntakeHandoff } from '@/features/scan/handoff';
import { clearIntake, peekIntake } from '@/features/scan/handoff';
import { PassDelivery } from '@/features/scan/reveal';
import { useFormatters } from '@/i18n/use-format';

/**
 * The register: the list of everybody, one search box, and the record screens it leads to.
 *
 * **It opens as a list rather than as a prompt.** An empty box asks the server for the first page
 * of the whole register, twenty at a time, ordered by name; typing narrows that list to a search.
 * The register used to answer an empty box with "type at least two characters", which meant the one
 * screen that answers "who is registered here" could not answer it without a name to try.
 *
 * **"The index is still loading" and "nobody matched" are different sentences** (AD-23). The API
 * answers 503 with `SYSTEM_SEARCH_INDEX_BUILDING` while the projection builds, rather than an empty
 * page, because an empty page is indistinguishable from "this person is not registered" — and the
 * operator's next move is to register them again, producing a second record with a second active
 * pass and phantom anomalies at the gate forever. The two states are rendered differently and a
 * `web`-tier case asserts each one does not say the other.
 *
 * **Every record action reached from here is acknowledged first** (AD-53), and the acknowledgment is
 * what marks the resulting audit row `manual_search`. Cancelling sends nothing at all, which is the
 * negative the whole mechanism depends on: a request issued after a declined acknowledgment would
 * put a `person.view_full` row in the trail for something nobody did, into a table that admits no
 * DELETE and that AD-37's exfiltration threshold counts.
 *
 * **The view lives in the URL**, so it survives a reload and can be sent to somebody — but the
 * SEARCH TERM does not. `table/url-state.ts` states why: that box is where an operator types a name
 * or a passport number, and a browser's address bar is a file on a shared terminal's disk.
 *
 * **The term is debounced before it reaches the server.** Without it every keystroke is a projection
 * query against the account's own budget, and the table unmounts between them, on the busiest screen
 * in the product.
 */

/** How long the register waits after the last keystroke before it asks. */
const SEARCH_DEBOUNCE_MS = 250;

/** Why the register has no rows to show. Four sentences, and never the wrong one. */
type EmptyReason = 'past_last_page' | 'no_search_matches' | 'no_filter_matches' | 'register_empty';

/**
 * Why this page is empty, as one sentence chosen in one place.
 *
 * Four ways to see no rows on the one screen whose doctrine is that an empty list must never read as
 * "this person is not registered", and they need four different sentences. The ORDER is the rule: a
 * page past the end explains itself whatever else is set, because a stale link is where it came
 * from; a search that matched nobody is about the term rather than about the register; and "there is
 * nobody here at all" is what is left when nothing was asked for.
 */
/**
 * The same question, asked only of an answer the server has actually given.
 *
 * **`keepPreviousData` holds the previous page during a request**, and that page paired with the
 * NEW view is how "nobody matched this search" gets announced for a search that has not come back
 * yet. The region is `aria-live`, so a sentence that is wrong for one round trip is a sentence that
 * is read out. On the one screen whose doctrine is that an empty list must never be described
 * wrongly, saying nothing while it is in flight is the honest state.
 */
function settledEmptyReason(
  register: PersonRegisterState,
  page: PersonPage | null,
  view: RegisterView,
  searched: string,
): EmptyReason | null {
  if (register.status !== 'results' && register.status !== 'empty') {
    return null;
  }
  return register.refreshing ? null : emptyReasonFor(page, view, searched);
}

function emptyReasonFor(
  page: PersonPage | null,
  view: RegisterView,
  searched: string,
): EmptyReason | null {
  if (page === null || page.items.length > 0) {
    return null;
  }
  if (page.page > 1 && page.page > page.pageCount) {
    return 'past_last_page';
  }
  if (searched.trim().length >= MIN_SEARCH_LENGTH) {
    return 'no_search_matches';
  }
  if (view.status !== null || view.presence !== null || view.photo !== 'any') {
    return 'no_filter_matches';
  }
  return 'register_empty';
}

/** The sentence each one is said with, as a total map, so a new reason cannot go unsaid. */
const EMPTY_MESSAGE_KEY = {
  past_last_page: 'person.table.pastLastPage',
  no_search_matches: 'search.noResults',
  no_filter_matches: 'person.table.noRowsMatch',
  register_empty: 'person.table.registerEmpty',
} as const satisfies Readonly<Record<EmptyReason, string>>;

/**
 * Where the operator came from, and therefore where "done here" takes them back to.
 *
 * The gate hands this screen a scan (`features/scan/handoff.ts`) and the operator's next act is at
 * the gate: the person they have just registered is standing at the door with the band that was
 * just bound to them. So a gate-originated screen returns to the gate on EVERY exit — the create
 * form's cancel, the record's own control, and an erasure — rather than on one of them, because a
 * rule with three quarters of an implementation is the quarter somebody meets.
 */
type Origin = 'register' | 'gate';

/** What the register is showing besides the table. */
type Screen =
  | { readonly kind: 'list' }
  | {
      readonly kind: 'create';
      /** A band the gate already scanned, seeded into the form, or `null`. */
      readonly bandCode: string | null;
      /** AD-53: how this registration was reached, which the audit row records. */
      readonly method: RecordActionMethod;
      readonly origin: Origin;
    }
  | {
      readonly kind: 'record';
      readonly personId: string;
      readonly editing: boolean;
      readonly origin: Origin;
      /**
       * How this record was REACHED, which is what AD-53's audit row records.
       *
       * A record opened straight after registering it was reported as `manual_search` — the
       * operator found it by searching the register — when they had just typed it in. Both tokens
       * are manual, so the manual-versus-scan ratio is unharmed; what is wrong is the row, in a
       * table that admits no DELETE.
       */
      readonly method: RecordActionMethod;
      /**
       * The pass this registration just issued, when it issued one.
       *
       * It is carried in the screen's own state rather than re-fetched, because that is the ONE
       * moment the code is in this browser legitimately: `credential.reveal` is admin-and-above, so
       * an operator who left this screen could never see it again. `null` covers both a pre-printed
       * registration taken before a band was to hand and an amendment, which issues nothing.
       */
      readonly issued?: IssuedCredential;
      /** True when the record was opened in order to erase it, which is a row action of its own. */
      readonly erasing?: boolean;
    };

/**
 * What a role that may not write is shown in place of a screen that writes.
 *
 * **One verb decides every write the register offers.** A new record and an amendment are both
 * `person:write`; a role without it (the analyst, which reads every record) is offered neither, and
 * a screen state that asks for one anyway falls back to what it may do: the list instead of the
 * registration form, and the record instead of its amendment. A pure function rather than a
 * condition at each branch, so the screen below reads one state and no branch can forget to ask.
 */
function readableBy(screen: Screen, mayWrite: boolean): Screen {
  if (mayWrite) {
    return screen;
  }
  if (screen.kind === 'create') {
    return { kind: 'list' };
  }
  return screen.kind === 'record' && screen.editing ? { ...screen, editing: false } : screen;
}

/** Whether this session may register or amend a person, which is one verb for both. */
function mayWritePeople(session: Session | null): boolean {
  return session !== null && roleHasPermission(session.user.role, 'person:write');
}

/**
 * The screen a hand-off from the gate opens on, or the list when there is none.
 *
 * A pure function of what was staged, so the one thing that decides half this screen's behaviour is
 * readable in one place rather than inferred from a `useState` initializer.
 */
function screenFromHandoff(handoff: IntakeHandoff | null): Screen {
  if (handoff === null) {
    return { kind: 'list' };
  }
  if (handoff.kind === 'register') {
    return {
      kind: 'create',
      bandCode: handoff.code,
      method: handoff.method,
      origin: 'gate',
    };
  }
  return {
    kind: 'record',
    personId: handoff.personId,
    editing: handoff.editing,
    method: handoff.method,
    origin: 'gate',
  };
}

/**
 * The same record, read or amended, with the one-shot members handled.
 *
 * **`erasing` is dropped and `issued` is kept, and the asymmetry is the whole reason this exists.**
 * `erasing` means "this record was opened BY the erasure row action", which `RecordScreen` reads
 * once as the dialog's initial state; carrying it through an amendment reopened the erase dialog on
 * a record the operator had just finished editing, because toggling `editing` remounts that screen
 * and re-runs the initializer. `issued` is the opposite: a minted code is in this browser for one
 * moment and `credential:reveal` is admin-and-above, so losing it on the way back from the
 * amendment screen would lose it for good.
 */
function reading(
  screen: Extract<Screen, { kind: 'record' }>,
  editing: boolean,
): Extract<Screen, { kind: 'record' }> {
  return {
    kind: 'record',
    personId: screen.personId,
    editing,
    method: screen.method,
    origin: screen.origin,
    ...(screen.issued === undefined ? {} : { issued: screen.issued }),
  };
}

export function PersonRegister(): ReactNode {
  const { t } = useTranslation();
  const format = useFormatters();
  const timezone = useReportingTimezone();
  const session = useSession();
  const mayWrite = mayWritePeople(session);
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();

  const view = useMemo(() => viewFromParams(params), [params]);
  const [term, setTerm] = useState('');
  const searched = useDebounced(term, SEARCH_DEBOUNCE_MS);
  /**
   * What the gate handed over, read ONCE and consumed on mount.
   *
   * The read is deliberately non-destructive and the clear is deliberately an effect.
   * `app/bootstrap.tsx` wraps the application in `<StrictMode>`, which invokes a `useState`
   * initializer twice on the first mount: a read that cleared would hand the second invocation
   * nothing, and because the `web` harness composes the providers of `app/app.tsx` rather than
   * `bootstrap.tsx` it runs WITHOUT StrictMode — so every test would pass while the browser landed
   * on an empty form. Clearing in an effect happens once per mount, so coming back to `/persons` by
   * any other route gets the list, which is what stops one person's band reaching the next person's
   * registration.
   */
  const [requested, setScreen] = useState<Screen>(() => screenFromHandoff(peekIntake()));
  const screen = readableBy(requested, mayWrite);
  useEffect(() => {
    clearIntake();
  }, []);
  const queries = useQueryClient();
  const [pending, setPending] = useState<{
    readonly action: ManualAction;
    readonly person: PersonSummary;
  } | null>(null);

  const register = usePersonRegister({
    term: searched,
    page: view.page,
    limit: ROWS_PER_PAGE,
    // Asked only while the list is what is on screen. This hook sits above the create and record
    // returns because a hook has to, and a page of real names fetched behind a registration form is
    // a request nobody made — on the one flow where somebody is standing at the desk.
    enabled: screen.kind === 'list',
    ...(view.status === null ? {} : { status: view.status }),
    ...(view.presence === null ? {} : { presence: view.presence }),
    ...(view.photo === 'any' ? {} : { photo: view.photo }),
    ...(view.sort === null
      ? {}
      : { sort: view.sort.columnId, direction: view.sort.descending ? 'desc' : 'asc' }),
  });
  const { definitions, loading: fieldsLoading, error: fieldsError } = useFieldDefinitions();
  // **The method the record was REACHED by, not a constant.** A record opened straight after
  // registering it was audited as `manual_search`, which says the operator found it by searching
  // the register when they had just typed it in — a wrong row in a table that admits no DELETE.
  // Both tokens are manual, so AD-53's ratio is unharmed either way; what is wrong is the record.
  const detail = usePersonDetail(
    screen.kind === 'record' ? screen.personId : null,
    screen.kind === 'record' ? screen.method : 'manual_search',
  );

  const setView = useCallback(
    (next: Parameters<typeof paramsFromView>[0]) => {
      setParams(paramsFromView(next), { replace: true });
    },
    [setParams],
  );

  /**
   * Finishing here, which for a gate-originated screen means going back to the gate.
   *
   * One helper rather than a destination written at each of the four exits, because the operator's
   * way out of a registration they started at a door has to be the same wherever they press it.
   * Returning them to the gate is also what keeps AD-53's ratio honest: the record screen offers a
   * manual movement, which is filed as `manual_search`, while the band they have just bound is about
   * to be scanned at that door and filed as the scan it is.
   */
  const leave = useCallback(
    (origin: Origin) => {
      if (origin === 'gate') {
        void navigate('/scan');
        return;
      }
      setScreen({ kind: 'list' });
    },
    [navigate, setScreen],
  );

  const page = register.status === 'results' || register.status === 'empty' ? register.page : null;
  const rows = register.status === 'results' ? register.page.items : [];
  const emptyReason = settledEmptyReason(register, page, view, searched);

  /**
   * Carrying out an action the operator has acknowledged.
   *
   * **Every one of them opens the RECORD**, including the movement and the erasure, and that is a
   * correction rather than a simplification. The movement used to navigate to the gate screen with
   * a person id in the query string, and the gate read neither parameter: its panel is rendered
   * from a scan's answer, which a person id cannot produce, and the only route that answers by id
   * is the audited full disclosure the record screen has already performed. The erasure needs the
   * record for a different reason — its dialog demands the record's own short reference typed out,
   * which nobody can supply from a list.
   */
  function perform(action: ManualAction, person: PersonSummary): void {
    setScreen({
      kind: 'record',
      personId: person.id,
      editing: action === 'modify',
      // Reached from the register's own list, which is a search.
      method: 'manual_search',
      origin: 'register',
      ...(action === 'shred' ? { erasing: true } : {}),
    });
  }

  // Memoized because `PersonTable` builds its column definitions from them: a callback recreated on
  // every render makes `useTable` re-derive every row model on every keystroke, which is the
  // opposite of what a table holding a page of a large register is for.
  const act = useCallback((action: ManualAction, person: PersonSummary) => {
    setPending({ action, person });
  }, []);

  const exportCsv = useCallback(
    (exported: readonly PersonSummary[]): void => {
      const csv = toCsv(
        [
          t('person.table.columnRef'),
          t('person.table.columnReference'),
          t('person.table.columnName'),
          t('person.table.columnStatus'),
          t('person.table.columnPresence'),
          t('person.table.columnLastActivity'),
        ],
        exported.map((person) => [
          person.shortRef,
          person.referenceCode ?? '',
          person.displayName,
          t(`person.table.status_${person.status}`),
          t(`person.table.presence_${person.presenceState}`),
          person.lastActivityAt === null ? '' : format.dateTime(person.lastActivityAt),
        ]),
      );
      saveText(csv, csvFileName('registro', new Date(), timezone), 'text/csv');
    },
    [format, t, timezone],
  );

  if (screen.kind === 'create') {
    return (
      <div className="flex flex-col gap-4">
        <PersonForm
          mode={{ kind: 'create' }}
          definitions={definitions}
          method={screen.method}
          initialBandCode={screen.bandCode}
          autoFocusFields={screen.origin === 'gate'}
          // **Handed to the form rather than rendered beside it, so it goes when the form does.**
          // Once a create commits there is nothing left to cancel, and a way out sitting one
          // element above the form is a way out that still works: pressed during the photograph's
          // upload it loses a minted pass code for good — `credential:reveal` is admin-and-above —
          // turns an attach failure into silence, and then drags the operator back a second later
          // when the upload finishes. The form knows when it has become terminal; the register does
          // not.
          leaveControl={
            <LeaveButton
              origin={screen.origin}
              onLeave={() => {
                leave(screen.origin);
              }}
            />
          }
          onCreated={(created) => {
            setScreen({
              kind: 'record',
              personId: created.person.id,
              editing: false,
              // How this registration was REACHED, carried through rather than restated. A
              // registration started at the gate is a scan, and the record it opens has to say so
              // — and carrying `origin` with it is what puts the operator back at the door when
              // they close the record rather than on a list they never asked for.
              method: screen.method,
              origin: screen.origin,
              // Delivering the pass is the last step of a registration, and the code is here now
              // or never: see the note on `Screen`.
              ...(created.credential === null ? {} : { issued: created.credential }),
            });
          }}
          onCancel={() => {
            leave(screen.origin);
          }}
        />
      </div>
    );
  }

  if (screen.kind === 'record') {
    const person = detail.person;
    return (
      <div className="flex flex-col gap-6">
        <LeaveButton
          origin={screen.origin}
          onLeave={() => {
            leave(screen.origin);
          }}
        />
        {detail.loading && (
          <p className="flex items-center gap-2 text-sm text-muted-foreground">
            <LoaderCircle aria-hidden className="size-4 animate-spin" />
            {t('common.loading')}
          </p>
        )}
        {detail.error !== null && detail.error !== undefined && (
          <FailureAlert error={detail.error} />
        )}
        {person !== null && screen.editing && (
          <AmendScreen
            person={person}
            definitions={definitions}
            method="manual_search"
            issued={screen.issued}
            // **Kept by the SCREEN rather than by the control that minted it.** A code is in this
            // browser for one moment and an operator can never fetch it back, so a reissue held
            // inside the amendment screen would be lost the instant they saved and it unmounted.
            onIssued={(credential) => {
              setScreen({ ...reading(screen, true), issued: credential });
            }}
            onAmended={(amended) => {
              // **The amended record is PUT INTO the cache rather than merely navigated back to.**
              // `usePersonDetail` is fifteen seconds fresh and deliberately never refetches on
              // window focus — `person.get` writes a `person.view_full` row on every call, into a
              // table that admits no DELETE and that AD-37's exfiltration threshold counts — so
              // nothing at all would have re-read this record, and the operator who had just
              // corrected a telephone number was shown the old one for fifteen seconds. Their next
              // move is to type it again, which writes a second revision for a correction that had
              // already worked. Seeding is better than invalidating here because the response
              // carries the same `personDetailSchema` the read returns: the screen is correct
              // immediately, at the cost of no request and no second audit row.
              queries.setQueryData(queryKeys.personDetail(person.id), amended);
              // The history is a separate read and an amendment writes a revision, so seeding the
              // record alone left "History of changes" saying one after the second was written.
              void queries.invalidateQueries({
                queryKey: queryKeys.personRevisions(person.id),
              });
              setScreen(reading(screen, false));
            }}
            onCancel={() => {
              setScreen(reading(screen, false));
            }}
          />
        )}
        {/*
          The pass, delivered. It appears only on the registration or the reissue that produced it,
          because the code is in this browser at that one moment and an operator cannot fetch it
          back: revealing a stored credential is `credential:reveal`, which is admin-and-above. The
          amendment screen renders its own copy from the same member, so exactly one is on screen.
        */}
        {person !== null && !screen.editing && screen.issued !== undefined && (
          <PassDelivery
            pass={{ kind: 'issued', issued: screen.issued, shortRef: person.shortRef }}
            personName={firstNameOf(person)}
          />
        )}
        {person !== null && !screen.editing && (
          <RecordScreen
            person={person}
            definitions={definitions}
            openErasure={screen.erasing === true}
            onAmend={() => {
              setScreen(reading(screen, true));
            }}
            onErased={() => {
              leave(screen.origin);
            }}
          />
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between gap-4">
        <h1 className="text-2xl font-semibold tracking-tight">{t('nav.persons')}</h1>
        {mayWrite && (
          <Button
            type="button"
            data-testid="new-person"
            onClick={() => {
              setScreen({
                kind: 'create',
                bandCode: null,
                // Typed in a moment ago, not found by searching for it, and not scanned.
                method: 'manual_key',
                origin: 'register',
              });
            }}
          >
            <Plus aria-hidden />
            {t('person.table.newPerson')}
          </Button>
        )}
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <div className="flex min-w-64 flex-col gap-2">
          <Label htmlFor="person-search">{t('person.table.searchLabel')}</Label>
          <div className="relative">
            <Search
              aria-hidden
              className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground"
            />
            <Input
              id="person-search"
              type="search"
              className="pl-9"
              value={term}
              onChange={(event) => {
                setTerm(event.target.value);
                setView({ ...view, page: 1 });
              }}
            />
          </div>
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="filter-status">{t('person.table.filterStatus')}</Label>
          <NativeSelect
            id="filter-status"
            value={view.status ?? ''}
            onChange={(event) => {
              const parsed = personStatusSchema.safeParse(event.target.value);
              setView({
                ...view,
                status: parsed.success ? parsed.data : null,
                page: 1,
              });
            }}
          >
            <option value="">{t('person.table.filterAny')}</option>
            {personStatusSchema.options.map((status) => (
              <option key={status} value={status}>
                {t(`person.table.status_${status}`)}
              </option>
            ))}
          </NativeSelect>
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="filter-presence">{t('person.table.filterPresence')}</Label>
          <NativeSelect
            id="filter-presence"
            value={view.presence ?? ''}
            onChange={(event) => {
              const parsed = presenceStateSchema.safeParse(event.target.value);
              setView({ ...view, presence: parsed.success ? parsed.data : null, page: 1 });
            }}
          >
            <option value="">{t('person.table.filterAny')}</option>
            {presenceStateSchema.options.map((state) => (
              <option key={state} value={state}>
                {t(`person.table.presence_${state}`)}
              </option>
            ))}
          </NativeSelect>
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="filter-photo">{t('person.table.filterPhoto')}</Label>
          <NativeSelect
            id="filter-photo"
            value={view.photo}
            onChange={(event) => {
              const chosen = event.target.value;
              setView({
                ...view,
                photo: chosen === 'with' || chosen === 'without' ? chosen : 'any',
                page: 1,
              });
            }}
          >
            <option value="any">{t('person.table.filterAny')}</option>
            <option value="with">{t('person.table.photoWith')}</option>
            <option value="without">{t('person.table.photoWithout')}</option>
          </NativeSelect>
        </div>

        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => {
            setView(DEFAULT_VIEW);
          }}
        >
          {t('person.table.clearFilters')}
        </Button>
      </div>

      {fieldsError !== null && !fieldsLoading && <FailureAlert error={fieldsError} />}

      {/*
        One letter is not a search, and the list below it is still the whole register: without this
        the screen looks as though it ignored what was typed.
      */}
      {term.trim().length > 0 && term.trim().length < MIN_SEARCH_LENGTH && (
        <p className="text-sm text-muted-foreground">{t('person.table.startTyping')}</p>
      )}

      {/*
        **Every outcome of the request is said HERE, in one live region.** An operator typing needs
        to be told what came back, a table cell is not a live region, and four empty sentences said
        in two places is how two of them end up disagreeing.
      */}
      <div aria-live="polite" data-testid="search-state">
        {register.status === 'loading' && (
          <p className="flex items-center gap-2 text-sm text-muted-foreground">
            <LoaderCircle aria-hidden className="size-4 animate-spin" />
            {t('common.loading')}
          </p>
        )}

        {register.status === 'index_building' && (
          <Alert role="status">
            <LoaderCircle aria-hidden className="animate-spin" />
            <AlertTitle>{t('search.indexBuilding')}</AlertTitle>
            <AlertDescription>{t('search.indexBuildingHint')}</AlertDescription>
          </Alert>
        )}

        {emptyReason !== null && (
          <p className="text-sm text-muted-foreground">{t(EMPTY_MESSAGE_KEY[emptyReason])}</p>
        )}

        {register.status === 'failed' && <FailureAlert error={register.error} />}

        {register.status === 'results' && register.page.refine && (
          <p className="text-xs text-muted-foreground">{t('search.refined')}</p>
        )}
      </div>

      {/*
        A page from a shared or hand-edited link that is past the end has to be recoverable without
        editing the address bar, so the one control that always works is offered beside the sentence.
      */}
      {(emptyReason === 'past_last_page' || (register.status === 'failed' && view.page > 1)) && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="self-start"
          data-testid="back-to-first-page"
          onClick={() => {
            setView({ ...view, page: 1 });
          }}
        >
          {t('person.table.firstPage')}
        </Button>
      )}

      {register.status === 'results' && session !== null && (
        <PersonTable
          rows={rows}
          page={{
            page: register.page.page,
            pageCount: register.page.pageCount,
            total: register.page.total,
          }}
          busy={register.refreshing}
          view={view}
          role={session.user.role}
          onViewChange={setView}
          onAction={act}
          onExport={exportCsv}
        />
      )}

      <AcknowledgmentDialog
        pending={pending}
        onConfirm={() => {
          if (pending !== null) {
            const { action, person } = pending;
            setPending(null);
            perform(action, person);
          }
        }}
        onCancel={() => {
          setPending(null);
        }}
      />
    </div>
  );
}

/**
 * The way out of a create or a record, pointed at wherever the operator came from.
 *
 * The two are a different CONTROL rather than one control with a different label, and the test id is
 * why: `packages/e2e/fixtures/flows.ts` presses `back-to-list` to get back to the register, and a
 * gate-originated screen does not go there. Naming them apart is what makes a helper that reaches
 * for the wrong one fail by not finding it, rather than by navigating somewhere nobody expected.
 */
function LeaveButton({
  origin,
  onLeave,
}: Readonly<{ origin: Origin; onLeave: () => void }>): ReactNode {
  const { t } = useTranslation();
  const toGate = origin === 'gate';
  return (
    <Button
      type="button"
      // Outlined rather than ghost on the way back to the gate: there it is the next step of the
      // intake rather than a way back out of a screen the operator chose to open.
      variant={toGate ? 'outline' : 'ghost'}
      size="sm"
      className="self-start"
      data-testid={toGate ? 'back-to-gate' : 'back-to-list'}
      onClick={onLeave}
    >
      {t(toGate ? 'person.record.backToGate' : 'person.record.backToList')}
    </Button>
  );
}
